import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ETSY_SHOP_URL } from "@/data/products";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || !isHome
          ? "bg-[#1a1412]/85 backdrop-blur-md border-b border-[#362b28]"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/eta-logo.jpg"
            alt="Earth Tone Archetypes"
            className="w-10 h-10 object-cover border border-[#c28b46]/40 group-hover:border-[#c28b46] transition-colors"
          />
          <span className="font-serif text-[15px] md:text-[16px] tracking-tight text-[#e8dcc4] leading-tight">
            Earth Tone <em className="text-[#c28b46] not-italic">Original Skin</em>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <a
            href={isHome ? "#manifesto" : "/#manifesto"}
            className="link-underline text-[12px] tracking-[0.22em] uppercase text-[#c8b99c] hover:text-[#e8dcc4]"
            data-testid="nav-manifesto"
          >
            Manifesto
          </a>
          <a
            href={isHome ? "#shop" : "/#shop"}
            className="link-underline text-[12px] tracking-[0.22em] uppercase text-[#c8b99c] hover:text-[#e8dcc4]"
            data-testid="nav-shop"
          >
            Collection
          </a>
          <Link
            to="/about"
            className="link-underline text-[12px] tracking-[0.22em] uppercase text-[#c8b99c] hover:text-[#e8dcc4]"
            data-testid="nav-about"
          >
            About
          </Link>
          <a
            href={isHome ? "#guide" : "/#guide"}
            className="link-underline text-[12px] tracking-[0.22em] uppercase text-[#c8b99c] hover:text-[#e8dcc4]"
            data-testid="nav-guide"
          >
            Free Guide
          </a>
        </nav>

        <a
          href={ETSY_SHOP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 border border-[#c28b46] text-[#c28b46] hover:bg-[#c28b46] hover:text-[#1a1412] transition-colors text-[11px] tracking-[0.22em] uppercase font-semibold"
          data-testid="nav-etsy-cta"
        >
          Shop Etsy
          <span aria-hidden>→</span>
        </a>

        <button
          type="button"
          className="md:hidden text-[#e8dcc4] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          <div className="w-6 h-[1px] bg-[#e8dcc4] mb-1.5" />
          <div className="w-6 h-[1px] bg-[#e8dcc4] mb-1.5" />
          <div className="w-4 h-[1px] bg-[#e8dcc4] ml-auto" />
        </button>
      </div>

      {open && (
        <div
          className="md:hidden bg-[#1a1412] border-t border-[#362b28] px-6 py-6 flex flex-col gap-5"
          data-testid="mobile-menu"
        >
          <a href={isHome ? "#manifesto" : "/#manifesto"} className="text-[13px] tracking-[0.22em] uppercase text-[#c8b99c]">Manifesto</a>
          <a href={isHome ? "#shop" : "/#shop"} className="text-[13px] tracking-[0.22em] uppercase text-[#c8b99c]">Collection</a>
          <Link to="/about" className="text-[13px] tracking-[0.22em] uppercase text-[#c8b99c]">About</Link>
          <a href={isHome ? "#guide" : "/#guide"} className="text-[13px] tracking-[0.22em] uppercase text-[#c8b99c]">Free Guide</a>
          <a
            href={ETSY_SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-terra justify-center mt-2"
            data-testid="mobile-etsy-cta"
          >
            Shop Etsy →
          </a>
        </div>
      )}
    </motion.header>
  );
}
